import {SelectCreatable} from '@wandb/weave/components/Form/Select';
import React, {useMemo} from 'react';
import {type RunHistoryKeyInfoKeys} from 'src/types/run';

import {useHistoryKeysQuery} from '../../../../state/graphql/historyKeysQuery';
import {isSystemMetric} from '../../../../util/metrics';
import {useEventStore} from './EventStore';

type HistoryKeyValue = RunHistoryKeyInfoKeys[string];

const RESULT_LIMIT = 100;
const RESULT_LIMIT_OPTION_VALUE = '__result_limit';

type MetricOption = {
  label: string | React.ReactNode;
  value: string;
  isDisabled?: boolean;
};

const toOption = (key: string): MetricOption => ({label: key, value: key});

const isNumericUserMetric = (key: string, info: HistoryKeyValue): boolean =>
  (info.typeCounts?.some(t => t.type === 'number') ?? false) &&
  !key.startsWith('_') &&
  !isSystemMetric(key);

export const buildMetricOptions = (
  keys: RunHistoryKeyInfoKeys
): MetricOption[] => {
  const options: string[] = [];
  for (const key in keys) {
    const info = keys[key];
    if (isNumericUserMetric(key, info)) {
      options.push(key);
    }
  }
  options.sort((a, b) => a.localeCompare(b));
  const limitedOptions = options.slice(0, RESULT_LIMIT);

  return [
    ...limitedOptions.map(key => toOption(key)),
    ...(limitedOptions.length < options.length
      ? [
          {
            label: `Limited to ${RESULT_LIMIT} items. Refine search to see other options.`,
            value: RESULT_LIMIT_OPTION_VALUE,
            isDisabled: true,
          },
        ]
      : []),
  ];
};

type MetricNameInputProps = {
  value: string | undefined;
  onChange: (value: string) => void;
};

export function MetricNameInput({value, onChange}: MetricNameInputProps) {
  // oxlint-disable-next-line wandb/zustand-use-store-selectors -- destructuring only two fields
  const {entityName, projectName} = useEventStore();

  const queryResult = useHistoryKeysQuery(
    {
      entityName: entityName ?? '',
      projectName: projectName ?? '',
      filters: {op: 'AND', filters: []},
      sort: {keys: []},
    },
    !entityName || !projectName
  );

  const options: MetricOption[] = useMemo(() => {
    if (queryResult.loading || queryResult.error) {
      return [];
    }
    return buildMetricOptions(queryResult.historyKeyInfo.keys);
  }, [queryResult]);

  const selectedOption = value ? toOption(value) : null;

  return (
    <SelectCreatable<MetricOption>
      data-test="metric-input"
      options={options}
      value={selectedOption}
      placeholder="Select metric"
      isLoading={queryResult.loading}
      onChange={(option: MetricOption | null) => {
        if (option && option.value !== RESULT_LIMIT_OPTION_VALUE) {
          onChange(option.value);
        }
      }}
      onCreateOption={(inputValue: string) => {
        const trimmedInputValue = inputValue.trim();
        if (trimmedInputValue.length > 0) {
          onChange(trimmedInputValue);
        }
      }}
      formatCreateLabel={(inputValue: string) => `Create "${inputValue}"`}
      createOptionPosition="first"
      isOptionDisabled={option => !!option.isDisabled}
    />
  );
}
